import { useLayoutEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useStore } from "../../js/state/store";
import { useAuth } from "../../js/hooks/useAuth";
import { useResponsive } from "../../js/hooks/useResponsive";
import { pageThemeClass } from "../../js/utils/pageThemes";
import TopNav from "../navigation/TopNav";
import AgentNav from "../navigation/AgentNav";
import BrokerNav from "../navigation/BrokerNav";
import BuyerNav from "../navigation/BuyerNav";
import SellerNav from "../seller/SellerNav";
import JourneySidebar from "../journey/JourneySidebar";
import FrameZoomControls from "../journey/FrameZoomControls";
import { JourneyWorkspaceProvider } from "../../js/hooks/useJourneyWorkspace";
import { FrameZoomProvider, useFrameZoom } from "../../js/hooks/useFrameZoom";
import { DeskThemeProvider, useDeskTheme } from "../../js/hooks/useDeskTheme";
import DeskThemeToggle from "../desk/DeskThemeToggle";
import { useClientPortal } from "../../js/hooks/useClientPortal";
import Footer from "./Footer";
import FrameReturnTop from "./FrameReturnTop";
import Sidebar from "./Sidebar";
import { useContainedScroll } from "../../js/hooks/useContainedScroll";
import BrokerScopeBanner from "../desk/BrokerScopeBanner";
import AccountGateHost from "../modals/AccountGateHost";
import GuestLockModal from "../modals/GuestLockModal";
import SystemInfoModal from "../modals/SystemInfoModal";
import ClientAccessModal from "../modals/ClientAccessModal";
import NotificationBell from "../messaging/NotificationBell";
import InstallInstructionsModal from "../modals/InstallInstructionsModal";
import OfficeLoginDialog from "../modals/OfficeLoginDialog";
import { useModal } from "../../js/hooks/useModal";
import BetaWelcomeModal from "../beta/BetaWelcomeModal";
import BetaDeskHint from "../beta/BetaDeskHint";
import CheckoutNotice from "../billing/CheckoutNotice";

function deskHomeForRole(role) {
  if (role === "broker") return "/broker/dashboard";
  if (role === "agent") return "/agent/dashboard";
  if (role === "admin") return "/admin";
  if (role === "seller") return "/seller/home";
  if (role === "buyer") return "/start";
  return "";
}

function RoleTag({ role }) {
  const label = role === "admin" ? "Admin/Dev" : role.charAt(0).toUpperCase() + role.slice(1);
  const home = deskHomeForRole(role);
  if (!home) {
    return <span className="badge role-tag">{label}</span>;
  }
  return (
    <Link to={home} className="badge role-tag" title={`Open ${label} desk`}>
      {label}
    </Link>
  );
}

export default function Header({ variant = "public" }) {
  const { state, dispatch } = useStore();
  const { session, logout } = useAuth();
  const { isDesktop } = useResponsive();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const brandSub =
    variant === "seller" || variant === "broker" || pathname === "/" ? "Journey" : "Checklist";

  return (
    <header className="site-header">
      <Link to={variant === "admin" ? "/admin" : "/"} className="brand">
        <span className="brand-mark-frame">
          <img
            src="/images/logo-mark.png"
            alt=""
            className="brand-mark"
            width="48"
            height="48"
            decoding="async"
            fetchPriority="high"
          />
        </span>
        <span className="brand-text">
          <span className="brand-name">DreamHome</span>
          <span className="brand-sub">{brandSub}</span>
        </span>
      </Link>

      {variant === "client" ? null : <TopNav />}

      <div className="header-actions">
        {variant === "public" && !session ? (
          <button
            type="button"
            className="header-pro-login"
            onClick={() => {
              dispatch({ type: "SET_PENDING_PATH", payload: "" });
              dispatch({ type: "OPEN_MODAL", payload: "officeLogin" });
            }}
          >
            Professional Login
          </button>
        ) : null}
        {variant === "client" ? null : (
          <div className="header-select-group" role="group" aria-label="Account">
            {session ? (
              <button
                type="button"
                className="header-select-item"
                onClick={async () => {
                  navigate("/dashboard", { replace: true });
                  await logout();
                }}
              >
                Sign out
              </button>
            ) : (
              <button
                type="button"
                className="header-select-item"
                onClick={() => dispatch({ type: "OPEN_MODAL", payload: "buyerAccount" })}
              >
                Sign in
              </button>
            )}
          </div>
        )}
        {variant === "client" ? null : session?.role ? <RoleTag role={session.role} /> : null}
        {variant === "client" ? null : session ? <NotificationBell /> : null}
        {variant !== "client" && !isDesktop && (
          <button
            type="button"
            className="menu-toggle"
            aria-label="Open menu"
            onClick={() => dispatch({ type: "TOGGLE_SIDEBAR" })}
          >
            ☰
          </button>
        )}
      </div>
      <BetaDeskHint />

      {!isDesktop && variant !== "agent" && variant !== "broker" && variant !== "client" && state.ui.sidebarOpen && (
        <nav className="top-nav is-open" onClick={() => dispatch({ type: "SET_SIDEBAR", payload: false })}>
          <TopNav />
        </nav>
      )}
    </header>
  );
}

export function AppLayout({ variant = "public", children, showBuyerNav = false, showSellerNav = false }) {
  const canTone =
    variant === "agent" || variant === "broker" || variant === "buyer" || variant === "seller" || variant === "admin";
  const layout = <AppLayoutInner variant={variant} showBuyerNav={showBuyerNav} showSellerNav={showSellerNav}>{children}</AppLayoutInner>;

  if (canTone) {
    return <DeskThemeProvider role={variant}>{layout}</DeskThemeProvider>;
  }
  return layout;
}

function AppLayoutInner({ variant, showBuyerNav, showSellerNav, children }) {
  const { pathname } = useLocation();
  const deskTone = useDeskTheme();
  const installModal = useModal("install");
  const isJourney = showBuyerNav || showSellerNav;
  const isDesk = variant === "agent" || variant === "broker" || variant === "admin";
  const journeyMode = showBuyerNav ? "buyer" : showSellerNav ? "seller" : null;
  const deskTheme = deskTone?.deskTheme;
  const portal = useClientPortal();
  const layoutVariant =
    portal.active && variant !== "agent" && variant !== "broker" && variant !== "admin" ? "client" : variant;
  let theme = pageThemeClass(pathname);
  if (variant === "admin") {
    theme = deskTheme === "light" ? "theme-agent" : "theme-admin";
  } else if (deskTheme === "dark") {
    theme = "theme-seller";
  } else if (deskTheme === "light") {
    theme = "theme-agent";
  }
  const toneClass = deskTheme ? ` desk-theme-${deskTheme}` : "";

  const shell = (
    <div
      className={`app-shell app-shell--${layoutVariant} ${theme}${isJourney ? " is-journey" : ""}${
        isDesk ? " is-desk" : ""
      }${toneClass}`}
    >
      <Header variant={layoutVariant} />
      {layoutVariant === "client" && portal.advisor ? (
        <p className="advisor-banner">{portal.advisor.displayName}</p>
      ) : null}
      <CheckoutNotice />
      {variant === "agent" && <AgentNav />}
      {variant === "broker" && <BrokerNav />}
      <div className={`app-body${isJourney ? " is-journey" : ""}${isDesk ? " is-desk" : ""}`}>
        {isDesk ? <Sidebar /> : null}
        <main className={`app-main${isJourney ? " is-journey" : ""}${isDesk ? " is-desk" : ""}`}>
          {isJourney ? (
            <JourneyFrame showBuyerNav={showBuyerNav} showSellerNav={showSellerNav}>
              {children}
            </JourneyFrame>
          ) : isDesk ? (
            <DeskFrame>
              {variant === "broker" ? <BrokerScopeBanner /> : null}
              {children}
            </DeskFrame>
          ) : (
            children
          )}
        </main>
        {isJourney ? <JourneySidebar /> : null}
      </div>
      <Footer />
      <OfficeLoginDialog />
      <AccountGateHost />
      <GuestLockModal />
      <SystemInfoModal />
      <ClientAccessModal />
      <BetaWelcomeModal />
      <InstallInstructionsModal isOpen={installModal.isOpen} onClose={installModal.close} />
    </div>
  );

  if (isJourney) {
    return (
      <FrameZoomProvider storageKey="dh_frame_zoom_v2">
        <JourneyWorkspaceProvider mode={journeyMode}>{shell}</JourneyWorkspaceProvider>
      </FrameZoomProvider>
    );
  }

  if (isDesk) {
    return <FrameZoomProvider storageKey="dh_desk_zoom_v2">{shell}</FrameZoomProvider>;
  }

  return shell;
}

function JourneyFrame({ showBuyerNav, showSellerNav, children }) {
  const { pathname } = useLocation();
  const { frameZoom } = useFrameZoom();
  const bodyRef = useRef(null);
  const { showTop, returnToTop } = useContainedScroll(bodyRef);

  useLayoutEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [pathname]);

  return (
    <div className="journey-frame" data-tour="journey-frame" style={{ "--frame-zoom": frameZoom }}>
      <div className="journey-frame__chrome">
        {showBuyerNav ? <BuyerNav /> : null}
        {showSellerNav ? <SellerNav /> : null}
        <div className="journey-frame__tools">
          <DeskThemeToggle />
          <FrameZoomControls />
        </div>
      </div>
      <div className="journey-frame__body" ref={bodyRef}>
        <div className="journey-frame__zoom-surface" style={{ zoom: frameZoom }}>
          {children}
        </div>
        <FrameReturnTop visible={showTop} onClick={returnToTop} />
      </div>
    </div>
  );
}

function DeskFrame({ children }) {
  const { pathname, hash } = useLocation();
  const { frameZoom } = useFrameZoom();
  const bodyRef = useRef(null);
  const { showTop, returnToTop } = useContainedScroll(bodyRef);

  useLayoutEffect(() => {
    const frame = bodyRef.current;
    if (!frame) return undefined;
    const id = hash.replace(/^#/, "");
    const target = id ? document.getElementById(id) : null;
    if (target && frame.contains(target)) {
      const surface = frame.querySelector(".desk-frame__zoom-surface");
      const first = surface?.firstElementChild;
      if (first && (target === first || first.contains(target))) {
        frame.scrollTop = 0;
        return undefined;
      }
      const zoom = Number(surface?.style?.zoom) || 1;
      const top =
        (target.getBoundingClientRect().top - frame.getBoundingClientRect().top) / zoom + frame.scrollTop;
      frame.scrollTop = Math.max(0, top);
      return undefined;
    }
    frame.scrollTop = 0;
    return undefined;
  }, [pathname, hash]);

  return (
    <div className="desk-frame" data-tour="desk-frame">
      <div className="desk-frame__chrome">
        <DeskThemeToggle />
        <FrameZoomControls />
      </div>
      <div className="desk-frame__body" ref={bodyRef}>
        <div className="desk-frame__zoom-surface" style={{ zoom: frameZoom }}>
          {children}
        </div>
      </div>
      <FrameReturnTop visible={showTop} onClick={returnToTop} />
    </div>
  );
}
